/**
 * usePageMeta Hook
 * 
 * Provides a way to set and access page metadata (title, description, etc.)
 * 
 * Usage in page components:
 * ```tsx
 * import { useSetPageMeta } from '../../../hooks/usePageMeta';
 * 
 * export default function Dashboard() {
 *   useSetPageMeta({
 *     title: 'Dashboard',
 *     description: 'Overview of your business metrics'
 *   });
 *   
 *   return <YourPageContent />;
 * }
 * ```
 * 
 * Then in Layout or any component:
 * ```tsx
 * const meta = usePageMeta();
 * console.log(meta.title); // "Dashboard"
 * ```
 */

import { useEffect } from 'react';

export interface PageMeta {
  title?: string;
  description?: string;
  [key: string]: unknown;
}

// Global state for page metadata
let currentPageMeta: PageMeta = {};
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach(listener => listener());
}

/**
 * Hook to set page metadata from a page component
 */
export function useSetPageMeta(meta: PageMeta): void {
  useEffect(() => {
    currentPageMeta = meta;
    notifyListeners();
    
    // Cleanup - reset to default when component unmounts
    return () => {
      currentPageMeta = {};
      notifyListeners();
    };
  }, [meta]);
}

/**
 * Hook to read current page metadata (used by Layout)
 */
export function usePageMeta(): PageMeta {
  // Force re-render when metadata changes
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  
  useEffect(() => {
    listeners.add(forceUpdate);
    return () => {
      listeners.delete(forceUpdate);
    };
  }, []);
  
  return currentPageMeta;
}

// Import React for useReducer
import React from 'react';
